import { registerOsAppLoader, type OsAppChunkLoader } from "./osAppPrefetch";

/**
 * Dynamic imports for OS window content, keyed by `APP_REGISTRY` app id.
 *
 * Specifiers must match the `lazy(() => import(...))` calls in OsAppRegistry
 * so Vite serves the same chunk when we prefetch.
 */
export const OS_APP_CHUNK_LOADERS: Record<string, OsAppChunkLoader> = {
  reports: () => import("@/routes/reports"),
  meetings: () => import("@/routes/meetings"),
  meeting: () =>
    import("@/components/meetings/MeetingWindow").then((m) => ({
      default: m.MeetingWindow,
    })),
  screener: () =>
    import("@/routes/screener").then((m) => ({
      default: m.ScreenerWindowContent,
    })),
  playground: () =>
    import("@/routes/playground").then((m) => ({
      default: m.PlaygroundWindowContent,
    })),
  documents: () => import("@/routes/documents"),
  document: () =>
    import("@/components/documents/DocumentEditorDrawer").then((m) => ({
      default: m.DocumentEditorDrawer,
    })),
  ops: () => import("@/routes/ops"),
  settings: () => import("@/routes/settings"),
  "report-builder": () =>
    import("@/routes/report-builder").then((m) => ({
      default: m.ReportBuilderWindowContent,
    })),
  workers: () => import("@/components/pods/WorkerDirectory"),
  worker: () => import("@/routes/worker"),
  home: () =>
    import("@/components/home/Home").then((m) => ({ default: m.Home })),
  calculators: () =>
    import("@/routes/calculators").then((m) => ({
      default: m.CalculatorsWindowContent,
    })),
  "mission-control": () =>
    import("@/components/mission-control/MissionControl").then((m) => ({
      default: m.MissionControl,
    })),
  artifacts: () =>
    import("@/components/artifacts/ArtifactsApp").then((m) => ({
      default: m.ArtifactsApp,
    })),
  /** Single-artefact editor window — must match OsAppRegistry lazy import + warm DocumentEditor. */
  artifact: () =>
    import("@/components/artifacts/ArtifactWindow").then((m) => {
      void import("@/components/artifacts/DocumentEditor");
      return { default: m.ArtifactWindow };
    }),
  "add-products": () =>
    import("@/components/add-products/AddProductsApp").then((m) => ({
      default: m.AddProductsApp,
    })),
  "task-review": () =>
    import("@/components/review/TaskReviewWorkspace").then((m) => ({
      default: m.TaskReviewWorkspace,
    })),
  "scenario-tracker": () =>
    import("@/components/scenarios/Scenarios").then((m) => ({
      default: m.Scenarios,
    })),
  "what-if": () => import("@/routes/what-if"),
  "cash-flow": () => import("@/routes/cash-flow"),
  "pulse-v2": () =>
    import("@/components/pulse/PulseDashboard").then((m) => ({
      default: m.PulseDashboard,
    })),
  "fund-processor": () => import("@/routes/fund-processor"),
  "equity-processor": () => import("@/routes/equity-processor"),
  "bond-processor": () => import("@/routes/bond-processor"),
  "custom-products": () => import("@/routes/custom-products"),
  "trade-blotter": () => import("@/routes/trade-blotter"),
  "portfolio-builder": () => import("@/routes/portfolio-builder"),
  "data-hub": () => import("@/routes/data-hub"),
  "advisor-dashboard": () =>
    import("@/components/meridian/AdvisorDashboard").then((m) => ({
      default: m.AdvisorDashboard,
    })),
  inbox: () =>
    import("@/components/inbox/Inbox").then((m) => ({ default: m.Inbox })),
  "monte-carlo": () =>
    import("@/components/monte-carlo").then((m) => ({ default: m.MonteCarlo })),
  cmas: () => import("@/components/cmas").then((m) => ({ default: m.CMAs })),
  optimiser: () =>
    import("@/components/optimiser").then((m) => ({ default: m.Optimiser })),
  "factor-analysis": () =>
    import("@/components/factor-analysis").then((m) => ({
      default: m.FactorAnalysis,
    })),
  "portfolio-lab": () =>
    import("@/components/portfolio-lab/PortfolioLab").then((m) => ({
      default: m.PortfolioLab,
    })),
  rebalancing: () =>
    import("@/components/rebalancing/RebalancingContainer").then((m) => ({
      default: m.RebalancingContainer,
    })),
  twr: () => import("@/components/twr").then((m) => ({ default: m.TWRContainer })),
  irr: () => import("@/components/irr").then((m) => ({ default: m.IRRContainer })),
};

const SCENARIO_TRACKER_TAB_APP_IDS = [
  "scenario-tracker-newsflow",
  "scenario-tracker-directionality",
  "scenario-tracker-heatmap",
  "scenario-tracker-deep-dive",
  "scenario-tracker-metric-projections",
] as const;

for (const [appId, loader] of Object.entries(OS_APP_CHUNK_LOADERS)) {
  registerOsAppLoader(appId, loader);
}

const scenarioLoader = OS_APP_CHUNK_LOADERS["scenario-tracker"];
if (scenarioLoader) {
  for (const appId of SCENARIO_TRACKER_TAB_APP_IDS) {
    registerOsAppLoader(appId, scenarioLoader);
  }
}
